<template>
  <div class="bg-gray-50 min-h-screen">
    <Header v-if="userInfo.user.permission === 'co-ordinator'" />
    <InstructorHeader v-if="userInfo.user.permission === 'instructor'" />
    <FlashMessage v-if="messages.text" :message="messages" />
    <HodHeader v-if="userInfo.user.permission === 'hod'" />
    <div class="flex min-h-screen">
      <!-- Sidebar -->
      <div class="w-64 bg-white border-r border-gray-200 p-4">
        <div class="relative" ref="newMenuRef">
          <button
            @click="showNewMenu = !showNewMenu"
            class="w-full mb-6 flex items-center gap-3 bg-white hover:bg-gray-50 border border-gray-300 rounded-full px-6 py-3 shadow-sm"
          >
            <svg
              class="w-5 h-5 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span class="font-medium text-gray-700">New</span>
          </button>
          <div
            v-if="showNewMenu"
            class="absolute left-20 top-10 mt-2 w-44 bg-white border rounded-lg shadow z-20"
          >
            <button
              @click="showNewFolderPopup = true"
              class="w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
            >
              New Folder
            </button>

            <button
              @click="openUploadPopup"
              class="w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
            >
              Upload File
            </button>
          </div>
        </div>

        <nav class="space-y-1">
          <router-link
            :to="{ name: 'StroageFilesView' }"
            class="flex items-center gap-3 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
              />
            </svg>
            <span>My Drive</span>
          </router-link>
        </nav>
      </div>

      <!-- Main Content -->
      <div class="flex-1 p-10 container mx-auto relative">
        <!-- Breadcrumbs -->
        <nav class="flex mb-4" aria-label="Breadcrumb">
          <ol class="inline-flex items-center space-x-1 md:space-x-3">
            <li
              v-for="(folder, index) in folderPath"
              :key="folder.id"
              class="inline-flex items-center"
            >
              <template v-if="index === 0">
                <router-link
                  :to="{ name: 'StroageFilesView' }"
                  class="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <svg
                    class="w-4 h-4 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                    />
                  </svg>
                  Home
                </router-link>
              </template>
              <template v-else>
                <svg
                  class="w-6 h-6 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <router-link
                  v-if="index < folderPath.length - 1"
                  :to="{ name: 'ChildPageView', params: { id: folder.id } }"
                  class="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  {{ folder.name }}
                </router-link>
                <span
                  v-else
                  class="ml-1 text-sm font-medium text-gray-500 md:ml-2"
                >
                  {{ folder.name }}
                </span>
              </template>
            </li>
          </ol>
        </nav>

        <div class="mb-6 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <h1 class="text-xl font-semibold text-blue-600">
              Files
              <span class="text-xs font-medium text-gray-500 px-2 py-2">
                ({{ itemCount }} items)
              </span>
            </h1>
          </div>

          <div class="flex items-center gap-4">
            <div class="relative">
              <input
                v-model="searchQuery"
                @input="getFolderDetails"
                type="text"
                placeholder="Search the files ..."
                class="w-64 rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none"
              />
              <svg
                class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
        <!-- VIEW MODE TOGGLE -->
        <div
          class="absolute top-6 right-10 flex gap-2 border rounded-xl bg-white shadow z-20"
        >
          <button @click="viewMode = 'grid'" class="px-2 py-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="20"
              viewBox="0 -960 960 960"
              width="20"
              :fill="viewMode === 'grid' ? 'black' : '#E7E7E7'"
              :class="viewMode !== 'grid' ? 'opacity-70' : ''"
            >
              <path
                d="M340-540H200q-33 0-56.5-23.5T120-620v-140q0-33 23.5-56.5T200-840h140q33 0 56.5 23.5T420-760v140q0 33-23.5 56.5T340-540Zm-140-80h140v-140H200v140Zm140 500H200q-33 0-56.5-23.5T120-200v-140q0-33 23.5-56.5T200-420h140q33 0 56.5 23.5T420-340v140q0 33-23.5 56.5T340-120Zm-140-80h140v-140H200v140Zm560-340H620q-33 0-56.5-23.5T540-620v-140q0-33 23.5-56.5T620-840h140q33 0 56.5 23.5T840-760v140q0 33-23.5 56.5T760-540Zm-140-80h140v-140H620v140Zm140 500H620q-33 0-56.5-23.5T540-200v-140q0-33 23.5-56.5T620-420h140q33 0 56.5 23.5T840-340v140q0 33-23.5 56.5T760-120Zm-140-80h140v-140H620v140ZM340-620Zm0 280Zm280-280Zm0 280Z"
              />
            </svg>
          </button>

          <button @click="viewMode = 'table'" class="px-2 py-1 border-l">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="20"
              viewBox="0 -960 960 960"
              width="20"
              :fill="viewMode === 'table' ? 'black' : '#E7E7E7'"
              :class="viewMode !== 'table' ? 'opacity-70' : ''"
            >
              <path
                d="M280-600v-80h560v80H280Zm0 160v-80h560v80H280Zm0 160v-80h560v80H280ZM160-600q-17 0-28.5-11.5T120-640q0-17 11.5-28.5T160-680q17 0 28.5 11.5T200-640q0 17-11.5 28.5T160-600Zm0 160q-17 0-28.5-11.5T120-480q0-17 11.5-28.5T160-520q17 0 28.5 11.5T200-480q0 17-11.5 28.5T160-440Zm0 160q-17 0-28.5-11.5T120-320q0-17 11.5-28.5T160-360q17 0 28.5 11.5T200-320q0 17-11.5 28.5T160-280Z"
              />
            </svg>
          </button>
        </div>
        <div>
          <div v-if="viewMode === 'grid'">
            <div
              v-if="
                (folderDetails.folders && folderDetails.folders.length) ||
                (folderDetails.files && folderDetails.files.length)
              "
              class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7"
            >
              <!-- FOLDERS -->
              <div
                v-for="folder in folderDetails.folders"
                :key="'folder-' + folder.id"
                class="group relative rounded-xl bg-white shadow-sm hover:shadow-md"
              >
                <router-link
                  :to="{ name: 'ChildPageView', params: { id: folder.id } }"
                  class="block"
                >
                  <div class="bg-gray-100 p-8 h-[170px]">
                    <img src="@/assets/folder.png" />
                  </div>

                  <div class="p-2">
                    <h3
                      class="text-sm font-medium truncate"
                      :title="folder.name"
                    >
                      {{ folder.name }}
                    </h3>
                  </div>
                </router-link>

                <div class="flex justify-between items-end px-2 pb-2">
                  <span class="text-xs text-gray-500">
                    {{ formatDate(folder.created_at) }}
                  </span>

                  <div class="relative">
                    <button
                      @click.stop="toggleMenu(folder.id)"
                      class="rounded p-1 hover:bg-gray-100"
                    >
                      ⋮
                    </button>

                    <div
                      v-if="activeMenuId === folder.id"
                      class="absolute right-0 top-6 w-32 bg-white border rounded-lg shadow z-10"
                    >
                      <button
                        @click="openRename(folder)"
                        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        Rename
                      </button>

                      <button
                        @click="deleteFolder(folder)"
                        class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 hidden"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- FILES  -->
              <div
                v-for="file in folderDetails.files"
                :key="'file-' + file.id"
                class="group relative rounded-xl bg-white shadow-sm hover:shadow-md"
              >
                <div
                  class="bg-gray-100 h-[170px] flex items-center justify-center p-8"
                >
                  <img
                    v-if="isImage(file.file)"
                    :src="file.file"
                    class="max-h-full max-w-full object-contain"
                  />

                  <img v-else :src="getFileIcon(file.file)" class="" />
                </div>

                <div class="p-2">
                  <h3 class="text-sm font-medium truncate cursor-pointerr">
                    {{ file.name }}
                  </h3>
                </div>

                <div class="flex justify-between items-end px-2 pb-2">
                  <span class="text-xs text-gray-500">
                    {{ formatDate(file.created_at) }}
                  </span>

                  <div class="relative">
                    <button
                      @click.stop="toggleMenu(file.id)"
                      class="rounded p-1 hover:bg-gray-100"
                    >
                      ⋮
                    </button>

                    <div
                      v-if="activeMenuId === file.id"
                      class="absolute right-0 top-6 w-32 bg-white border rounded-lg shadow z-10"
                    >
                      <button
                        @click="handlePreview(file)"
                        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        Preview
                      </button>
                      <button
                        @click="downloadFile(file)"
                        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        Download
                      </button>
                      <button
                        @click="openRenameFile(file)"
                        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        Rename
                      </button>
                      <button
                        @click="openDeletePopup(file)"
                        class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 hidden"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="flex flex-col items-center justify-center py-20 text-center text-gray-500"
            >
              <p class="text-lg font-medium">No files or folders</p>
            </div>
          </div>
          <div
            v-else-if="viewMode === 'table'"
            class="bg-white rounded-lg border shadow-sm"
          >
            <table class="w-full text-sm">
              <thead class="bg-gray-50 text-gray-600">
                <tr>
                  <th class="text-left px-4 py-3">Name</th>
                  <th class="text-left px-4 py-3">Type</th>
                  <th class="text-left px-4 py-3">Created</th>
                  <th class="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="folder in folderDetails.folders"
                  :key="'folder-' + folder.id"
                  class="border-t hover:bg-gray-50"
                >
                  <td class="px-4 py-3 flex items-center gap-2">
                    <img src="@/assets/folder.png" class="w-6 h-6" />
                    <router-link
                      :to="{ name: 'ChildPageView', params: { id: folder.id } }"
                      class="text-blue-600 hover:underline truncate"
                    >
                      {{ folder.name }}
                    </router-link>
                  </td>

                  <td class="px-4 py-3">Folder</td>

                  <td class="px-4 py-3 text-gray-500">
                    {{ formatDate(folder.created_at) }}
                  </td>

                  <td class="px-4 py-3 text-right relative">
                    <button
                      @click.stop="toggleMenu(folder.id)"
                      class="px-2 py-1 rounded hover:bg-gray-100"
                    >
                      ⋮
                    </button>

                    <div
                      v-if="activeMenuId === folder.id"
                      class="absolute right-0 top-8 w-32 bg-white border rounded-lg shadow z-20"
                    >
                      <button
                        @click="openRename(folder)"
                        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        Rename
                      </button>

                      <button
                        @click="deleteFolder(folder)"
                        class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 hidden"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>

                <tr
                  v-for="file in folderDetails.files"
                  :key="'file-' + file.id"
                  class="border-t hover:bg-gray-50"
                >
                  <td class="px-4 py-3 flex items-center gap-2">
                    <div>
                      <img
                        v-if="isImage(file.file)"
                        :src="file.file"
                        class="w-6 h-6"
                      />

                      <img
                        v-else
                        :src="getFileIcon(file.file)"
                        class="w-6 h-6"
                      />
                    </div>
                    <span
                      class="truncate cursor-pointer"
                      @click="handlePreview(file)"
                    >
                      {{ file.name }}
                    </span>
                  </td>

                  <td class="px-4 py-3">File</td>

                  <td class="px-4 py-3 text-gray-500">
                    {{ formatDate(file.created_at) }}
                  </td>

                  <td class="px-4 py-3 text-right relative">
                    <button
                      @click.stop="toggleMenu(file.id)"
                      class="px-2 py-1 rounded hover:bg-gray-100"
                    >
                      ⋮
                    </button>

                    <div
                      v-if="activeMenuId === file.id"
                      class="absolute right-0 top-8 w-36 bg-white border rounded-lg shadow z-20"
                    >
                      <button
                        @click="handlePreview(file)"
                        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        Preview
                      </button>

                      <button
                        @click="openRenameFile(file)"
                        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        Rename
                      </button>

                      <button
                        @click="downloadFile(file)"
                        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        Download
                      </button>

                      <!-- <button
                        @click="openDeletePopup(file)"
                        class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button> -->
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              v-if="
                (!folderDetails.folders ||
                  folderDetails.folders.length === 0) &&
                (!folderDetails.files || folderDetails.files.length === 0)
              "
              class="py-16 text-center text-gray-500"
            >
              No files or folders
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- New Folder Modal -->
    <div
      v-if="showNewFolderPopup"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <form
        @submit.prevent="createNewFolder"
        class="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
      >
        <h2 class="text-xl font-semibold mb-4">New Folder</h2>

        <input
          v-model="newFolderName"
          class="w-full px-4 py-2 border rounded-lg mb-4"
          placeholder="Folder name"
        />

        <div class="flex justify-end gap-3">
          <button
            type="button"
            @click="showNewFolderPopup = false"
            class="px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="disable_submit"
            class="px-4 py-2 text-sm bg-blues text-white rounded-lg"
          >
            Create
          </button>
        </div>
      </form>
    </div>

    <!-- Rename Modal -->
    <div
      v-if="renameFolderData"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 class="text-xl font-semibold mb-4">Rename Folder</h2>

        <input
          v-model="renameValue"
          class="w-full px-4 py-2 border rounded-lg mb-4"
        />

        <div class="flex justify-end gap-3">
          <button @click="renameFolderData = null" class="px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            @click="confirmRename"
            class="px-4 py-2 text-sm bg-blues text-white rounded-lg"
          >
            Rename
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div
      v-if="showDeletePopup"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 class="text-xl font-semibold mb-4">Delete Folder</h2>
        <p class="mb-6 text-gray-600">
          Are you sure you want to delete "{{ folderToDelete?.name }}"?
        </p>

        <div class="flex justify-end gap-3">
          <button @click="showDeletePopup = false" class="px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            @click="confirmDelete"
            class="px-4 py-2 text-sm bg-red-600 text-white rounded-lg"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
    <!-- Upload File Popup -->
    <div
      v-if="showPopup"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <!-- UPLOAD POPUP -->
        <form
          v-if="popupType === 'upload'"
          @submit.prevent="uploadFileByChunk()"
        >
          <h2 class="text-xl font-semibold mb-4">Upload File</h2>

          <input type="file" ref="uploadInput" class="mb-4 max-w-full" />

          <div v-if="uploading" class="mt-4">
            <div class="flex justify-between text-sm mb-1">
              <span>{{ file?.name }}</span>
              <span>{{ uploadProgress }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="bg-blues h-2 rounded-full transition-all"
                :style="{ width: uploadProgress + '%' }"
              ></div>
            </div>
          </div>

          <div class="flex justify-end mt-6 gap-6">
            <button
              type="button"
              @click="showPopup = false"
              :disabled="uploading"
              class="px-4 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200"
            >
              Close
            </button>

            <button
              type="submit"
              :disabled="disable_submit"
              :class="[
                'rounded-lg text-sm px-4 py-2',
                disable_submit
                  ? 'bg-gray-200 text-black cursor-not-allowed'
                  : 'bg-blues hover:bg-hover cursor-pointer text-white',
              ]"
            >
              Upload
            </button>
          </div>
        </form>

        <!-- DELETE POPUP -->
        <div v-else>
          <h2 class="text-xl font-semibold mb-4">Delete File</h2>

          <p class="text-gray-600 mb-6">
            Are you sure you want to delete
            <strong>{{ fileToDelete?.name }}</strong
            >?
          </p>

          <div class="flex justify-end gap-4">
            <button
              @click="showPopup = false"
              class="px-4 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200"
            >
              Cancel
            </button>

            <button
              @click="confirmDeleteFile"
              class="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="renameFileData"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 class="text-xl font-semibold mb-4">Rename File</h2>

        <input
          v-model="renameFileValue"
          class="w-full px-4 py-2 border rounded-lg mb-4"
        />

        <div class="flex justify-end gap-3">
          <button
            @click="renameFileData = false"
            class="px-4 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            @click="confirmRenameFile"
            :disabled="disable_submit"
            :class="[
              'px-4 py-2 text-sm rounded-lg text-white',
              disable_submit
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blues hover:bg-blue-800',
            ]"
          >
            Rename
          </button>

          <img
            v-if="disable_submit"
            src="@/assets/loading.gif"
            alt="loading"
            class="w-6 h-6 object-contain mt-2"
          />
        </div>
      </div>
    </div>
    <!-- Preview Modal (Image / PDF) -->
    <div
      v-if="showPreviewModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div
        class="bg-white rounded-xl w-[95%] max-w-4xl shadow-lg flex flex-col"
        :class="previewFileType === 'pdf' ? 'h-[90vh]' : ''"
      >
        <div class="flex justify-between items-center px-4 py-2 border-b">
          <h2 class="text-lg font-semibold">
            {{
              previewFileType === "image"
                ? "Image Preview"
                : previewFileType === "pdf"
                ? "PDF Preview"
                : "Document Preview"
            }}
          </h2>
        </div>
        <div class="p-4 flex-1 overflow-auto">
          <!-- IMAGE -->
          <img
            v-if="previewFileType === 'image'"
            :src="previewFileUrl"
            class="max-h-[500px] mx-auto object-contain"
          />

          <!-- PDF -->
          <embed
            v-else-if="previewFileType === 'pdf'"
            :src="previewFileUrl"
            type="application/pdf"
            class="w-full h-full"
          />
          <video
            v-else-if="previewFileType === 'video'"
            :src="previewFileUrl"
            controls
            playsinline
            class="w-full h-full bg-black"
          ></video>

          <!-- WORD / EXCEL -->
          <iframe
            v-else
            :src="`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
              previewFileUrl,
            )}`"
            class="w-full h-full min-h-[600px]"
            frameborder="0"
          ></iframe>
        </div>

        <div class="flex justify-end px-4 py-3 border-t">
          <button
            @click="showPreviewModal = false"
            class="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
    <!-- Download Popup -->
    <div
      v-if="showDownloadPopup"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div class="bg-white rounded-xl w-full max-w-sm p-6 shadow-lg">
        <h2 class="text-lg font-semibold mb-4">File Preview</h2>

        <div class="flex items-center gap-3 mb-6">
          <img :src="getFileIcon(fileToDownload.file)" class="w-10 h-10" />
          <p class="text-sm font-medium truncate">
            {{ fileToDownload.name }}
          </p>
        </div>

        <div class="flex justify-end gap-3">
          <button
            @click="showDownloadPopup = false"
            class="px-4 py-2 text-sm rounded bg-gray-200 hover:bg-gray-300"
          >
            Cancel
          </button>

          <button
            @click="downloadFile(fileToDownload)"
            class="px-4 py-2 text-sm rounded bg-blues text-white hover:bg-hover"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "@/components/Coordinate/Header.vue";
import FlashMessage from "@/components/FlashMessage.vue";
import axios from "axios";
import { mapGetters } from "vuex";
import InstructorHeader from "@/components/Instructor/Header.vue";
import HodHeader from "@/components/Hod/Header.vue";
export default {
  components: { Header, FlashMessage, InstructorHeader, HodHeader },

  data() {
    return {
      folderId: null,
      folderDetails: {},
      folderPath: [],
      activeMenuId: null,
      showNewFolderPopup: false,
      showDeletePopup: false,
      searchQuery: "",
      newFolderName: "",
      disable_submit: false,
      renameFolderData: null,
      renameValue: "",
      showNewMenu: false,
      folderToDelete: null,
      messages: { text: "", type: null },
      file: null,
      chunkSize: 2 * 1024 * 1024,
      chunkIndex: 0,
      totalChunks: 0,
      uploading: false,
      showPopup: false,
      popupType: null,
      renameFileData: null,
      renameFileValue: "",
      showPreviewModal: false,
      previewFileUrl: null,
      previewFileType: null,
      fileToDownload: null,
      showDownloadPopup: false,
      viewMode: "grid",
    };
  },
  watch: {
    "$route.params.id"(id) {
      this.folderId = id;
      this.getFolderDetails();
    },
  },

  computed: {
    ...mapGetters(["userInfo"]),
    itemCount() {
      const folders = this.folderDetails.folders?.length || 0;
      const files = this.folderDetails.files?.length || 0;
      return folders + files;
    },
    uploadProgress() {
      if (this.totalChunks === 0) return 0;
      return Math.round((this.chunkIndex / this.totalChunks) * 100);
    },
  },

  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
    this.folderId = this.$route.params.id;
    this.getFolderDetails();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    handlePreview(file) {
      const url = file.file.toLowerCase();

      const isPreviewable =
        /\.(jpg|jpeg|png|gif|webp|pdf|doc|docx|xls|xlsx|mp4|mov|avi|mkv|webm)$/i.test(
          url,
        );

      if (isPreviewable) {
        this.previewFileUrl = file.file;

        if (url.endsWith(".pdf")) this.previewFileType = "pdf";
        else if (/\.(jpg|jpeg|png|gif|webp)$/.test(url))
          this.previewFileType = "image";
        else if (/\.(mp4|mov|avi|mkv|webm)$/.test(url))
          this.previewFileType = "video";
        else this.previewFileType = "office";

        this.showPreviewModal = true;
      } else {
        this.fileToDownload = file;
        this.showDownloadPopup = true;
      }
      this.activeMenuId = null;
    },

    downloadFile(file) {
      const link = document.createElement("a");
      link.href = file.file;
      link.download = file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.activeMenuId = null;
    },
    async confirmRenameFile() {
      this.disable_submit = true;

      try {
        await axios.patch(
          `${process.env.VUE_APP_API}/drive-file/${this.renameFileData.id}/rename/?Key=${this.userInfo.token_key}`,
          { name: this.renameFileValue },
        );

        this.messages = {
          text: "File renamed successfully",
          type: "success",
        };

        this.renameFileData = null;
        this.renameFileValue = "";
        this.getFolderDetails();
      } catch (error) {
        console.error("File rename failed", error);
        this.messages = {
          text: "Failed to rename file",
          type: "error",
        };
      } finally {
        this.disable_submit = false;
        setTimeout(() => {
          this.messages = { text: "", type: null };
        }, 1500);
      }
    },

    openRenameFile(file) {
      this.renameFileData = file;
      this.renameFileValue = file.name;
      this.activeMenuId = null;
    },
    async confirmDeleteFile() {
      try {
        await axios.delete(
          `${process.env.VUE_APP_API}/user/file_update/${this.fileToDelete.id}/?Key=${this.userInfo.token_key}`,
        );

        this.messages = {
          text: "File deleted successfully",
          type: "success",
        };

        this.fileToDelete = null;
        this.showPopup = false;
        this.getFolderDetails();

        setTimeout(() => {
          this.messages = { text: "", type: null };
        }, 1500);
      } catch (error) {
        console.error("Delete failed", error);
        this.messages = {
          text: "Failed to delete file",
          type: "error",
        };
        setTimeout(() => {
          this.messages = { text: "", type: null };
        }, 1500);
      }
    },
    getFileIcon(fileUrl) {
      if (!fileUrl) return require("@/assets/folder.png");
      const ext = fileUrl.split(".").pop().toLowerCase();
      if (ext === "csv") return require("@/assets/Admin/csv-file.png");
      if (["xls", "xlsx"].includes(ext))
        return require("@/assets/Admin/excel.png");
      if (ext === "pdf") return require("@/assets/Admin/pdf.svg");
      if (["ppt", "pptx"].includes(ext))
        return require("@/assets/Admin/powerpoint.png");
      if (["mp4", "mov", "avi", "mkv"].includes(ext))
        return require("@/assets/Admin/video-camera.png");
      if (["doc", "docx"].includes(ext))
        return require("@/assets/Admin/word.png");
      if (["zip", "rar", "7z"].includes(ext))
        return require("@/assets/Admin/zip.png");
      if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext))
        return require("@/assets/Admin/video-camera.png");

      return require("@/assets/folder.png");
    },

    isImage(fileUrl) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl);
    },

    handleOutsideClick(e) {
      if (!e.target.closest("[data-menu-trigger]")) {
        this.activeMenuId = null;
      }
      if (
        this.showNewMenu &&
        this.$refs.newMenuRef &&
        !this.$refs.newMenuRef.contains(e.target)
      ) {
        this.showNewMenu = false;
      }
    },
    openUploadPopup() {
      this.popupType = "upload";
      this.showPopup = true;
    },
    openDeletePopup(file) {
      this.fileToDelete = file;
      this.popupType = "delete";
      this.showPopup = true;
      this.activeMenuId = null;
    },

    closeUploadPopup() {
      if (!this.uploading) {
        this.showUploadPopup = false;
        this.file = null;
      }
    },
    uploadFileByChunk() {
      const input = this.$refs.uploadInput;
      const file = input.files[0];
      if (!file) return;
      this.file = file;
      this.chunkIndex = 0;
      this.totalChunks = Math.ceil(file.size / this.chunkSize);
      this.uploading = true;
      this.disable_submit = true;
      this.sendChunk();
    },

    sendChunk() {
      const start = this.chunkIndex * this.chunkSize;
      const end = Math.min(this.file.size, start + this.chunkSize);
      const chunk = this.file.slice(start, end);

      const formData = new FormData();
      formData.append("file", chunk);
      formData.append("filename", this.file.name);
      formData.append("chunkIndex", this.chunkIndex);
      formData.append("totalChunks", this.totalChunks);

      formData.append("user", this.userInfo.user.id);
      formData.append("folder", this.$route.params.id);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.VUE_APP_API}/file/creation/chunk/`, true);

      xhr.onload = () => {
        if (xhr.status === 200) {
          this.chunkIndex++;

          if (this.chunkIndex < this.totalChunks) {
            this.sendChunk();
          } else {
            this.uploading = false;

            this.messages = {
              text: "File uploaded successfully",
              type: "success",
            };

            this.$refs.uploadInput.value = "";
            this.file = null;
            this.showPopup = false;
            setTimeout(() => {
              this.messages = { text: "", type: "" };
            }, 3000);
            this.closeUploadPopup();
            this.disable_submit = false;
            this.getFolderDetails();
          }
        } else {
          this.uploading = false;
          this.messages = {
            text: "Upload failed",
            type: "error",
          };
          setTimeout(() => {
            this.messages = { text: "", type: null };
          }, 1500);
        }
      };

      xhr.onerror = () => {
        this.uploading = false;
        this.messages = {
          text: "Network error",
          type: "error",
        };
      };

      xhr.send(formData);
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    toggleMenu(id) {
      this.activeMenuId = this.activeMenuId === id ? null : id;
    },

    async getFolderDetails() {
      const res = await axios.get(
        `${process.env.VUE_APP_API}/folder/file/list/?Key=${this.userInfo.token_key}&folder_id=${this.folderId}&search=${this.searchQuery}`,
      );
      this.folderDetails = res.data;
      this.folderPath = res.data.folder_path || [];
    },

    createNewFolder() {
      this.disable_submit = true;

      axios
        .post(
          `${process.env.VUE_APP_API}/folder/creation/?Key=${this.userInfo.token_key}`,
          {
            name: this.newFolderName,
            parent: this.folderId,
          },
        )
        .then((response) => {
          this.disable_submit = false;
          if (response.data.status == 200) {
            this.messages = {
              text: response.data.message,
              type: "success",
            };
            this.newFolderName = "";
            this.getFolderDetails();
            this.showNewFolderPopup = false;
            setTimeout(() => {
              this.messages = { text: "", type: null };
            }, 1500);
          } else {
            this.messages = {
              text: response.data.message,
              type: "error",
            };
            setTimeout(() => {
              this.messages = { text: "", type: null };
            }, 1500);
          }
        });
    },

    openRename(file) {
      this.renameFolderData = file;
      this.renameValue = file.name;
      this.activeMenuId = null;
    },

    async confirmRename() {
      try {
        await axios.patch(
          `${process.env.VUE_APP_API}/folder/update/${this.renameFolderData.id}/?Key=${this.userInfo.token_key}`,
          { name: this.renameValue },
        );

        this.messages = {
          text: "Folder renamed successfully",
          type: "success",
        };

        this.renameFolderData = null;
        this.renameValue = "";
        this.getFolderDetails();

        setTimeout(() => {
          this.messages = { text: "", type: null };
        }, 1500);
      } catch (error) {
        console.error("Rename failed", error);
        this.messages = {
          text: "Failed to rename folder",
          type: "error",
        };
        setTimeout(() => {
          this.messages = { text: "", type: null };
        }, 1500);
      }
    },
    deleteFolder(file) {
      this.folderToDelete = file;
      this.showDeletePopup = true;
      this.activeMenuId = null;
    },

    async confirmDelete() {
      try {
        await axios.delete(
          `${process.env.VUE_APP_API}/folder/update/${this.folderToDelete.id}/?Key=${this.userInfo.token_key}`,
        );

        this.messages = {
          text: "Folder deleted successfully",
          type: "success",
        };

        this.showDeletePopup = false;
        this.folderToDelete = null;
        this.getFolderDetails();

        setTimeout(() => {
          this.messages = { text: "", type: null };
        }, 1500);
      } catch (error) {
        console.error("Delete failed", error);
        this.messages = {
          text: "Failed to delete folder",
          type: "error",
        };
        setTimeout(() => {
          this.messages = { text: "", type: null };
        }, 2000);
      }
    },
  },
};
</script>
